import Dialog from 'components/Dialog';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {usePostApi} from 'common/hooks/usePostApi';
import api from 'api';
import history from 'redux/history';
import {packId} from 'common/diplomatIdUtil';
import actions from 'features/layout/redux/actions';
import FormikTextField from 'components/inputs/FormikTextField';
import {useTranslation} from 'react-i18next';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {IDiplomatObject} from 'types/DiplomatObject';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {ITransaction} from 'types/diplomat/transaction/Transaction';
import {layoutSelectors} from 'features/layout/redux';
import {useModal} from 'common/hooks/useModal';
import {ConfirmationDialog} from 'components/dialogs/ConfirmationDialog';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {trim} from 'lodash';

type CreateTransactionProps = {
   open?: boolean;
   header: string;
   data?: ITransaction;
   objectType?: 'INBOUND_TRANSACTION' | 'OUTBOUND_TRANSACTION';
   onClose: () => void;
};

type INameForm = {name: string};

const CreateTransactionDialog = ({
   open = true,
   header,
   data,
   objectType,
   onClose,
}: CreateTransactionProps) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();

   const [formValues, setFormValues] = React.useState<INameForm>();

   const inboundFolderId = useSelector(layoutSelectors.inboundTransactionFolderId);
   const outboundFolderId = useSelector(layoutSelectors.outboundTransactionFolderId);

   const onSuccess = (data: ITransaction) => {
      dispatch(actions.loadTreeNav());
      onClose();
      history.push(`/${packId(data.objectType, data.id)}`);
   };
   const {request} = usePostApi<ITransaction>(api.transactions, {onSuccess, onFailure: onClose});
   const handleSubmit = (values?: INameForm) => {
      const payload = {
         transactionName: trim(values?.name || formValues?.name),
         transactionType:
            objectType === DiplomatObjectType.enum.INBOUND_TRANSACTION ? 'Inbound' : 'Outbound',
         overwrite: !values?.name,
         folderId:
            data?.id ||
            (objectType === DiplomatObjectType.enum.INBOUND_TRANSACTION
               ? inboundFolderId
               : outboundFolderId),
      };
      // if (!payload.folderId) {
      //    delete payload.folderId;
      // }
      request(JSON.stringify(payload));
   };

   const {open: alertOpened, handleOpen: openAlert, handleClose: closeAlert} = useModal();
   const transactions = useSelector(layoutSelectors.transactionsSyncsSelector);

   const helpersRef = React.useRef<FormOnSubmitHelpers<INameForm> | null>(null);

   const validateAndSave = (values: INameForm, helpers: FormOnSubmitHelpers<INameForm>) => {
      setFormValues(values);
      const {name} = values;
      const transaction = transactions.find((obj: IDiplomatObject) => obj.name === name);
      if (transaction) {
         if (transaction.objectType !== objectType) {
            helpers.setErrors({
               name: t('Diplomat already contains a {{type}} transaction named {{name}}', {
                  type: DiplomatObjectType[objectType!],
                  name,
               }),
            });
            helpers.setSubmitting(false);
         } else {
            // Object.assign(helpersRef.current, helpers);
            helpersRef.current = helpers;
            openAlert();
         }
         return;
      }

      return handleSubmit(values);
   };

   const onCloseAlert = React.useCallback(() => {
      helpersRef.current?.setSubmitting(false);
      closeAlert();
   }, [helpersRef, closeAlert]);

   return (
      <>
         <Dialog maxWidth="sm" header={header} open={open} close={onClose}>
            <Formik initialValues={{name: ''}} onSubmit={validateAndSave}>
               <>
                  <FormikTextField autoFocus label={t('New Transaction Name:')} name={'name'} />
                  <FormFooterButtons cancel={onClose} />
               </>
            </Formik>
         </Dialog>
         <ConfirmationDialog
            open={alertOpened}
            content={t(
               'Diplomat already contains a transactions with this name. \n Do you want to override it?'
            )}
            onOk={handleSubmit}
            onCancel={onCloseAlert}
         />
      </>
   );
};

export default CreateTransactionDialog;
